import { useState } from 'react';
import { navigate, resetNav, type SettingsPage } from '../../app/navigation';
import { useT } from '../../i18n/i18n';
import { useStore } from '../../state/createStore';
import { sessionStore, signOutAction } from '../../state/sessionStore';
import { Button, ConfirmSheet, ListRow, SectionEyebrow, TopBar } from '../../ui/components';
import type { IconName } from '../../ui/icons';

// Settings tab root (Owner task book 2026-09-04 §20): App Language · Account & Security · Sync &
// Storage · Photos & Camera · Plan & Subscription · Help & Support · Privacy · Terms · About · Sign
// Out · Delete Account. Every row opens its own page; nothing here changes the field flow. The
// language is changed ONLY here (no per-page switch, §3).
const ROWS: ReadonlyArray<{ page: SettingsPage; icon: IconName; group: 'app' | 'account' | 'legal' }> = Object.freeze([
  { page: 'language', icon: 'language', group: 'app' },
  { page: 'profile', icon: 'shield', group: 'account' },
  { page: 'report', icon: 'picture_as_pdf', group: 'account' },
  // 2026-10-19 (Owner item 33): the inspector's own reusable wording.
  { page: 'comments', icon: 'notes', group: 'account' },
  { page: 'account', icon: 'account_circle', group: 'account' },
  { page: 'sync', icon: 'cloud_off', group: 'account' },
  { page: 'photos', icon: 'photo_library', group: 'app' },
  { page: 'plan', icon: 'credit_card', group: 'account' },
  { page: 'help', icon: 'support_agent', group: 'legal' },
  { page: 'privacy', icon: 'privacy_tip', group: 'legal' },
  { page: 'terms', icon: 'gavel', group: 'legal' },
  { page: 'about', icon: 'info', group: 'legal' }
]);

export function settingsPageTitle(t: ReturnType<typeof useT>['t'], page: SettingsPage): string {
  switch (page) {
    case 'language':
      return t('shell.appLanguage');
    case 'profile':
      return t('settings.profile');
    case 'report':
      return t('settings.report');
    case 'comments':
      return t('comments.title');
    case 'account':
      return t('settings.account');
    case 'sync':
      return t('settings.sync');
    case 'photos':
      return t('settings.photos');
    case 'plan':
      return t('settings.plan');
    case 'help':
      return t('settings.help');
    case 'privacy':
      return t('settings.privacy');
    case 'terms':
      return t('settings.terms');
    case 'about':
      return t('settings.about');
  }
}

export function SettingsScreen() {
  const { t } = useT();
  const session = useStore(sessionStore, (s) => s);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  async function handleSignOut() {
    setConfirmSignOut(false);
    await signOutAction();
    resetNav({ screen: 'signIn' });
  }

  const subtitleOf = (page: SettingsPage): string => {
    switch (page) {
      case 'language':
        return t('settings.languageBody');
      case 'profile':
        return t('settings.profileBody');
      case 'report':
        return t('settings.reportBody');
      case 'comments':
        return t('comments.subtitle');
      case 'account':
        return session.email ?? t('settings.accountBody');
      case 'sync':
        return t('settings.syncBody');
      case 'photos':
        return t('settings.photosBody');
      case 'plan':
        return t('settings.planBody');
      case 'help':
        return t('settings.helpBody');
      case 'privacy':
        return t('settings.privacyBody');
      case 'terms':
        return t('settings.termsBody');
      case 'about':
        return t('settings.aboutBody');
    }
  };

  return (
    <div className="screen" data-testid="settings-screen">
      <TopBar title={t('nav.settings')} subtitle={session.email ?? undefined} />
      <div className="screen-body">
        <div className="list settings-list" data-testid="settings-list">
          {(['app', 'account', 'legal'] as const).map((group) => (
            <div className="settings-group" key={group}>
              <SectionEyebrow className="settings-group-label">{t(group === 'app' ? 'settings.groupApp' : group === 'account' ? 'settings.groupAccount' : 'settings.groupLegal')}</SectionEyebrow>
              {ROWS.filter((row) => row.group === group).map((row) => (
                <ListRow
                  key={row.page}
                  icon={row.icon}
                  iconTone="soft"
                  title={settingsPageTitle(t, row.page)}
                  subtitle={subtitleOf(row.page)}
                  chevron
                  onClick={() => navigate({ screen: 'settingsPage', page: row.page })}
                  testId={`settings-row-${row.page}`}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="screen-actions settings-actions">
          <Button variant="secondary" icon="logout" onClick={() => setConfirmSignOut(true)} testId="settings-sign-out">
            {t('settings.signOut')}
          </Button>
          <Button variant="danger" icon="person_off" onClick={() => navigate({ screen: 'settingsPage', page: 'account' })} testId="settings-delete-account">
            {t('settings.deleteAccount')}
          </Button>
        </div>
      </div>
      {confirmSignOut ? (
        <ConfirmSheet title={t('settings.signOutTitle')} body={t('settings.signOutBody')} confirmLabel={t('settings.signOut')} onConfirm={() => void handleSignOut()} onCancel={() => setConfirmSignOut(false)} />
      ) : null}
    </div>
  );
}
